import { required, useListContext, useTranslate } from "ra-core";
import { AutocompleteInput } from "@/components/admin/autocomplete-input";
import { CreateButton } from "@/components/admin/create-button";
import { Create } from "@/components/admin/create";
import { DataTable } from "@/components/admin/data-table";
import { DateInput } from "@/components/admin/date-input";
import { Edit } from "@/components/admin/edit";
import { List } from "@/components/admin/list";
import { MobileRecordCard } from "@/components/admin/mobile-record-card";
import { NumberInput } from "@/components/admin/number-input";
import { ReferenceField } from "@/components/admin/reference-field";
import { ReferenceInput } from "@/components/admin/reference-input";
import { SelectInput } from "@/components/admin/select-input";
import { SimpleForm } from "@/components/admin/simple-form";
import { TextInput } from "@/components/admin/text-input";
import { Card, CardContent } from "@/components/ui/card";

import { TopToolbar } from "../../atomic-crm/layout/TopToolbar";
import {
  BILLING_PERIODS,
  findChoice,
  SUBSCRIPTION_CATEGORIES,
  SUBSCRIPTION_STATUSES,
} from "../common/constants";
import { useFormatDate, useFormatMoney } from "../common/components";

const monthly = (s: { amount: number; billing_period: string }) =>
  s.billing_period === "monthly"
    ? Number(s.amount)
    : s.billing_period === "yearly"
      ? Number(s.amount) / 12
      : 0;

const Totals = () => {
  const { data } = useListContext();
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const active = (data ?? []).filter((s) => s.status === "active");
  const perMonth = active.reduce((sum, s) => sum + monthly(s), 0);
  return (
    <div className="mb-3 flex gap-6 text-sm">
      <p>
        <span className="text-muted-foreground">
          {translate("hub.subscriptions.monthly_total")}:
        </span>{" "}
        <span className="font-semibold">{formatMoney(perMonth)}</span>
      </p>
      <p>
        <span className="text-muted-foreground">
          {translate("hub.subscriptions.yearly_total")}:
        </span>{" "}
        <span className="font-semibold">{formatMoney(perMonth * 12)}</span>
      </p>
    </div>
  );
};

const SubscriptionList = () => {
  const translate = useTranslate();
  const formatMoney = useFormatMoney();
  const formatDate = useFormatDate();
  return (
    <List
      resource="subscriptions"
      sort={{ field: "next_renewal", order: "ASC" }}
      perPage={100}
      actions={
        <TopToolbar>
          <CreateButton label="hub.subscriptions.new" />
        </TopToolbar>
      }
    >
      <Totals />
      <DataTable
        rowClick="edit"
        bulkActionButtons={false}
        mobileCard={(s) => (
          <MobileRecordCard
            title={s.name}
            subtitle={s.provider}
            trailing={
              <span className="text-sm font-semibold tabular-nums">
                {formatMoney(s.amount)}
              </span>
            }
            meta={[
              translate(
                findChoice(BILLING_PERIODS, s.billing_period)?.name ?? "",
              ),
              translate(
                findChoice(SUBSCRIPTION_CATEGORIES, s.category)?.name ?? "",
              ),
              s.next_renewal ? formatDate(s.next_renewal) : null,
              translate(
                findChoice(SUBSCRIPTION_STATUSES, s.status)?.name ?? "",
              ),
            ]}
          />
        )}
      >
        <DataTable.Col source="name" />
        <DataTable.Col source="provider" />
        <DataTable.Col
          source="category"
          render={(s) =>
            translate(
              findChoice(SUBSCRIPTION_CATEGORIES, s.category)?.name ?? "",
            )
          }
        />
        <DataTable.Col
          source="amount"
          render={(s) =>
            `${formatMoney(s.amount)} · ${translate(
              findChoice(BILLING_PERIODS, s.billing_period)?.name ?? "",
            )}`
          }
        />
        <DataTable.Col
          source="next_renewal"
          render={(s) => formatDate(s.next_renewal)}
        />
        <DataTable.Col source="company_id">
          <ReferenceField
            source="company_id"
            reference="companies"
            link="show"
          />
        </DataTable.Col>
        <DataTable.Col
          source="status"
          render={(s) =>
            translate(findChoice(SUBSCRIPTION_STATUSES, s.status)?.name ?? "")
          }
        />
      </DataTable>
    </List>
  );
};

const SubscriptionInputs = () => (
  <div className="flex flex-col gap-4">
    <div className="grid gap-4 sm:grid-cols-2 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <TextInput source="name" validate={required()} />
      <TextInput source="provider" />
    </div>
    <div className="grid gap-4 sm:grid-cols-4 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <SelectInput
        source="category"
        choices={SUBSCRIPTION_CATEGORIES}
        validate={required()}
      />
      <NumberInput source="amount" step={0.01} min={0} validate={required()} />
      <SelectInput
        source="billing_period"
        choices={BILLING_PERIODS}
        validate={required()}
      />
      <DateInput source="next_renewal" />
    </div>
    <div className="grid gap-4 sm:grid-cols-3 [&>*]:min-w-0 [&>*>*]:min-w-0">
      <SelectInput
        source="status"
        choices={SUBSCRIPTION_STATUSES}
        validate={required()}
      />
      <ReferenceInput source="company_id" reference="companies">
        <AutocompleteInput
          label="resources.subscriptions.fields.company_id"
          optionText="name"
        />
      </ReferenceInput>
      <ReferenceInput source="deal_id" reference="deals">
        <AutocompleteInput
          label="resources.subscriptions.fields.deal_id"
          optionText="name"
        />
      </ReferenceInput>
    </div>
    <TextInput source="url" />
    <TextInput source="notes" multiline rows={3} />
  </div>
);

const SubscriptionCreate = () => (
  <Create
    resource="subscriptions"
    redirect="list"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm
          className="max-w-none"
          defaultValues={{
            category: "software",
            billing_period: "monthly",
            status: "active",
            currency: "EUR",
            amount: 0,
          }}
        >
          <SubscriptionInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Create>
);

const SubscriptionEdit = () => (
  <Edit
    resource="subscriptions"
    redirect="list"
    mutationMode="pessimistic"
    className="mx-auto w-full max-w-3xl"
  >
    <Card>
      <CardContent>
        <SimpleForm className="max-w-none">
          <SubscriptionInputs />
        </SimpleForm>
      </CardContent>
    </Card>
  </Edit>
);

export default {
  list: SubscriptionList,
  create: SubscriptionCreate,
  edit: SubscriptionEdit,
  recordRepresentation: "name",
};
